import { useEffect, useRef, useState } from "react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

let mapsLoaded = false;
let mapsPromise = null;

const loadGoogleMaps = () => {
  if (mapsLoaded && window.google?.maps) return Promise.resolve();
  if (mapsPromise) return mapsPromise;
  mapsPromise = new Promise((resolve, reject) => {
    if (window.google?.maps) { mapsLoaded = true; resolve(); return; }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=marker`;
    script.async = true;
    script.defer = true;
    script.onload = () => { mapsLoaded = true; resolve(); };
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
  return mapsPromise;
};

const GuideMap = ({ places, locationName, theme }) => {
  const mapRef = useRef(null);
  const [error, setError] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  const validPlaces = (places || []).filter(p => p.latitude && p.longitude);

  useEffect(() => {
    if (!MAPS_KEY || validPlaces.length === 0) return;

    let map = null;

    const initMap = async () => {
      try {
        await loadGoogleMaps();
        if (!mapRef.current || !window.google?.maps) return;

        const bounds = new window.google.maps.LatLngBounds();

        map = new window.google.maps.Map(mapRef.current, {
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
            { featureType: "water", elementType: "geometry.fill", stylers: [{ color: "#d4eef7" }] },
            { featureType: "landscape.natural", elementType: "geometry.fill", stylers: [{ color: "#f0f4e8" }] },
          ],
        });

        const infoWindow = new window.google.maps.InfoWindow();

        validPlaces.forEach((place, idx) => {
          const position = { lat: place.latitude, lng: place.longitude };
          bounds.extend(position);

          const marker = new window.google.maps.Marker({
            position,
            map,
            title: place.name,
            label: {
              text: String(idx + 1),
              color: "#FFFFFF",
              fontSize: "12px",
              fontWeight: "bold",
            },
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: theme?.accent || "#2F5D3A",
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
              scale: 16,
            },
          });

          const imgSrc = place.images?.[0] || place.image || "";
          const imgTag = imgSrc ? `<img src="${imgSrc}" style="width:180px;height:100px;object-fit:cover;border-radius:8px;margin-bottom:8px;" />` : "";

          marker.addListener("click", () => {
            infoWindow.setContent(`
              <div style="max-width:200px;font-family:system-ui,sans-serif;">
                ${imgTag}
                <p style="font-weight:700;font-size:14px;margin:0 0 4px 0;color:#1a1a1a;">${place.name}</p>
                <p style="font-size:12px;color:#888;margin:0;">${place.category || ""}</p>
                ${place.location_name ? `<p style="font-size:11px;color:#aaa;margin:2px 0 0 0;">${place.location_name}</p>` : ""}
              </div>
            `);
            infoWindow.open(map, marker);
          });
        });

        if (validPlaces.length > 1) {
          map.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
        } else if (validPlaces.length === 1) {
          map.setCenter({ lat: validPlaces[0].latitude, lng: validPlaces[0].longitude });
          map.setZoom(13);
        }

        setMapReady(true);
      } catch (err) {
        console.error("Map error:", err);
        setError(true);
      }
    };

    initMap();
  }, [validPlaces.length]);

  // Fallback if no API key or no places with coords
  if (!MAPS_KEY || validPlaces.length === 0) {
    const fallbackUrl = locationName
      ? `https://maps.google.com/maps?q=${encodeURIComponent(locationName + ", Maharashtra, India")}&t=&z=12&ie=UTF8&iwloc=&output=embed`
      : null;
    if (!fallbackUrl) return null;
    return (
      <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm bg-gray-50">
        <iframe src={fallbackUrl} width="100%" height="380" style={{ border: 0 }} allowFullScreen loading="lazy" title="Map" />
      </div>
    );
  }

  if (error) return null;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-sm bg-gray-50" data-testid="guide-interactive-map">
      <div ref={mapRef} style={{ width: "100%", height: "400px" }} />
      {/* Legend */}
      {mapReady && validPlaces.length > 0 && (
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-md px-3 py-2 max-w-[260px] max-h-[140px] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Places</p>
          {validPlaces.map((p, i) => (
            <div key={p.id} className="flex items-center gap-2 py-0.5">
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: theme?.accent || "#2F5D3A" }}>
                {i + 1}
              </span>
              <span className="text-xs text-gray-700 truncate">{p.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GuideMap;
